import type { InsertToPosition } from '@blocksuite/affine-shared/utils';
import type { GeneralServiceIdentifier } from '@blocksuite/global/di';
import { measureGfxPerformance } from '@blocksuite/std/gfx';
import { computed, type ReadonlySignal, signal } from '@preact/signals-core';

import type { GroupBy } from '../common/types.js';
import type {
  DataSourceCellsWindow,
  DataSourceCellsWindowRequest,
  DataSourceRowsWindow,
  DataSourceViewWindowState,
  DataSourceWindowRequest,
} from '../data-source/base.js';
import {
  type DatabaseProjectionQuery,
  type DatabaseProjectionResult,
  getProjectionPropertyIds,
  projectDatabaseRows,
} from '../data-source/projection.js';
import {
  getDataSourcePerformanceScope,
  getOrCreateProjectionCacheEntry,
  projectionCacheKey,
} from '../data-source/projection-cache.js';
import {
  clampDataSourceWindowLimit,
  clampDataSourceWindowOffset,
  DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT,
  DATABASE_LARGE_DATA_POLICY,
  normalizeDataSourcePropertyWindow,
} from '../data-source/window.js';
import type { Variable } from '../expression/types.js';
import { evalFilter } from '../filter/eval.js';
import type { FilterGroup } from '../filter/types.js';
import type { PropertyMetaConfig } from '../property/property-config.js';
import type { Sort } from '../sort/types.js';
import type { TraitKey } from '../traits/key.js';
import type { DatabaseFlags } from '../types.js';
import { computedLock } from '../utils/lock.js';
import type { DataViewDataType, ViewMeta } from '../view/data-view.js';
import { type Cell, CellBase } from './cell.js';
import type { Property } from './property.js';
import { type Row, RowBase } from './row.js';
import type { ViewManager } from './view-manager.js';

export type MainProperties = {
  titleColumn?: string;
  iconColumn?: string;
  imageColumn?: string;
};

export type SingleViewRowsWindow = DataSourceRowsWindow & {
  rows: Row[];
};

export type SingleViewProjectionResult = DatabaseProjectionResult & {
  rows: Row[];
};

export type SingleViewPropertiesWindow = {
  offset: number;
  limit: number;
  totalCount: number;
  propertyIds: string[];
  properties: Property[];
};

function normalizeConfiguredWindowLimit({
  fallbackLimit,
  maxLimit,
  requestedLimit,
}: {
  fallbackLimit: number;
  maxLimit: number;
  requestedLimit: number | undefined;
}): number {
  const boundedMax = Math.max(1, maxLimit);
  const fallback = Math.min(Math.max(1, fallbackLimit), boundedMax);
  if (
    requestedLimit === undefined ||
    !Number.isInteger(requestedLimit) ||
    requestedLimit <= 0
  ) {
    return fallback;
  }
  return Math.min(requestedLimit, boundedMax);
}

function clampConfiguredWindowLimit(limit: number, maxLimit: number): number {
  if (!Number.isInteger(limit) || limit <= 0) {
    throw new RangeError(`Window limit must be a positive integer`);
  }
  if (!Number.isInteger(maxLimit) || maxLimit <= 0) {
    throw new RangeError(`Window max limit must be a positive integer`);
  }
  return Math.min(limit, maxLimit);
}

export interface SingleView {
  data$: any;
  readonly id: string;
  readonly type: string;
  readonly manager: ViewManager;
  readonly meta: ViewMeta;
  readonly readonly$: ReadonlySignal<boolean>;

  delete(): void;

  duplicate(): void;

  readonly name$: ReadonlySignal<string>;

  nameSet(name: string): void;

  readonly detailProperties$: ReadonlySignal<Property[]>;
  readonly rowCount$: ReadonlySignal<number>;
  readonly columnCount$: ReadonlySignal<number>;
  readonly rowWindowOffset$: ReadonlySignal<number>;
  readonly rowWindowLimit$: ReadonlySignal<number>;
  readonly propertyWindowOffset$: ReadonlySignal<number>;
  readonly propertyWindowLimit$: ReadonlySignal<number>;
  setRowWindowOffset(offset: number): void;
  setRowWindowLimit(limit: number): void;
  setPropertyWindowOffset(offset: number): void;
  setPropertyWindowLimit(limit: number): void;
  projectRows(query: DatabaseProjectionQuery): SingleViewProjectionResult;
  getRowsWindow(request: DataSourceWindowRequest): SingleViewRowsWindow;
  getBoundedRowsWindow(): SingleViewRowsWindow;
  getRowIdsWindow(request: DataSourceWindowRequest): DataSourceRowsWindow;
  getBoundedRowIdsWindow(): DataSourceRowsWindow;
  getPropertiesWindow(
    request: DataSourceWindowRequest
  ): SingleViewPropertiesWindow;
  getBoundedPropertiesWindow(): SingleViewPropertiesWindow;
  getRawPropertiesWindow(
    request: DataSourceWindowRequest
  ): SingleViewPropertiesWindow;
  getBoundedRawPropertiesWindow(): SingleViewPropertiesWindow;
  getCellsWindow(request: DataSourceCellsWindowRequest): DataSourceCellsWindow;

  readonly vars$: ReadonlySignal<Variable[]>;

  readonly featureFlags$: ReadonlySignal<DatabaseFlags>;

  propertyGetOrCreate(propertyId: string): Property;
  rowGetOrCreate(rowId: string): Row;
  cellGetOrCreate(rowId: string, propertyId: string): Cell;

  rowAdd(insertPosition: InsertToPosition): string;
  rowCanAdd(): boolean;
  rowAddDisabledReason(): string | undefined;
  rowsDelete(rows: string[]): void;

  readonly propertyMetas$: ReadonlySignal<PropertyMetaConfig[]>;

  readonly search$: ReadonlySignal<string>;

  setSearch(str: string): void;

  propertyAdd(
    toAfterOfProperty: InsertToPosition,
    ops?: {
      type?: string;
      name?: string;
    }
  ): string | undefined;

  serviceGet<T>(key: GeneralServiceIdentifier<T>): T | null;
  serviceGetOrCreate<T>(key: GeneralServiceIdentifier<T>, create: () => T): T;

  traitGet<T>(key: TraitKey<T>): T | undefined;

  mainProperties$: ReadonlySignal<MainProperties>;

  lockRows(lock: boolean): void;

  isLocked$: ReadonlySignal<boolean>;
}

export function resolveSearchRowWindowOffset(
  previousSearch: string,
  nextSearch: string,
  currentOffset: number
): number {
  return previousSearch === nextSearch ? currentOffset : 0;
}

export abstract class SingleViewBase<
  ViewData extends DataViewDataType = DataViewDataType,
> implements SingleView {
  private readonly searchString = signal('');

  readonly search$: ReadonlySignal<string> = this.searchString;

  private readonly traitMap = new Map<symbol, unknown>();

  data$ = computed(() => {
    return this.dataSource.viewDataGet(this.id) as ViewData | undefined;
  });

  abstract detailProperties$: ReadonlySignal<Property[]>;

  protected lockRows$ = signal(false);

  isLocked$ = computed(() => {
    return this.lockRows$.value;
  });

  abstract mainProperties$: ReadonlySignal<MainProperties>;

  name$: ReadonlySignal<string> = computed(() => {
    return this.data$.value?.name ?? '';
  });

  propertyIds$: ReadonlySignal<string[]> = computed(() => {
    return this.properties$.value.map(v => v.id);
  });

  propertyMap$: ReadonlySignal<Record<string, Property>> = computed(() => {
    return Object.fromEntries(this.properties$.value.map(v => [v.id, v]));
  });

  abstract properties$: ReadonlySignal<Property[]>;

  abstract propertiesRaw$: ReadonlySignal<Property[]>;

  abstract readonly$: ReadonlySignal<boolean>;

  private get largeDataWindow() {
    return (this.data$.value as { largeDataWindow?: unknown } | undefined)
      ?.largeDataWindow as
      | {
          rowOffset?: number;
          rowLimit?: number;
          propertyOffset?: number;
          propertyLimit?: number;
        }
      | undefined;
  }

  private get effectiveLargeDataWindow() {
    return this.dataSource.getViewWindowState(this.id) ?? this.largeDataWindow;
  }

  private setLargeDataWindowPatch(patch: DataSourceViewWindowState): void {
    if (this.dataSource.setViewWindowState(this.id, patch)) {
      return;
    }
    const current = this.largeDataWindow ?? {};
    this.dataUpdate(
      () =>
        ({
          largeDataWindow: {
            ...current,
            ...patch,
          },
        }) as unknown as Partial<ViewData>
    );
  }

  rowWindowOffset$: ReadonlySignal<number> = computed(() => {
    return this.effectiveLargeDataWindow?.rowOffset ?? 0;
  });

  rowWindowLimit$: ReadonlySignal<number> = computed(() => {
    return normalizeConfiguredWindowLimit({
      fallbackLimit: DATABASE_LARGE_DATA_POLICY.ui.defaultPageSize,
      maxLimit: DATABASE_LARGE_DATA_POLICY.ui.maxPageSize,
      requestedLimit: this.effectiveLargeDataWindow?.rowLimit,
    });
  });

  propertyWindowOffset$: ReadonlySignal<number> = computed(() => {
    return this.effectiveLargeDataWindow?.propertyOffset ?? 0;
  });

  propertyWindowLimit$: ReadonlySignal<number> = computed(() => {
    return normalizeConfiguredWindowLimit({
      fallbackLimit: DATABASE_LARGE_DATA_POLICY.ui.columnPageSize,
      maxLimit: DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT,
      requestedLimit: this.effectiveLargeDataWindow?.propertyLimit,
    });
  });

  rowsRaw$ = computed(() => {
    return this.getBoundedRowsWindow().rows;
  });

  rawRowsWindow$ = computed(() => {
    const offset = clampDataSourceWindowOffset(
      this.dataSource.rowCount$.value,
      this.rowWindowOffset$.value,
      this.rowWindowLimit$.value
    );
    return this.dataSource
      .getRowsWindow({
        offset,
        limit: this.rowWindowLimit$.value,
      })
      .rowIds.map(id => this.rowGetOrCreate(id));
  });

  rows$ = computedLock(
    computed(() => {
      return this.rowsRaw$.value;
    }),
    this.isLocked$
  );

  rowsDelete(rows: string[]): void {
    this.lockRows(false);
    this.dataSource.rowDelete(rows);
  }

  rowCanAdd(): boolean {
    return this.dataSource.rowCanAdd();
  }

  rowAddDisabledReason(): string | undefined {
    return this.dataSource.rowAddDisabledReason();
  }

  rowIds$ = computed(() => {
    return this.rowsRaw$.value.map(v => v.rowId);
  });

  rowCount$: ReadonlySignal<number> = computed(
    () => this.dataSource.rowCount$.value
  );

  columnCount$: ReadonlySignal<number> = computed(
    () => this.dataSource.columnCount$.value
  );

  vars$ = computed(() => {
    return this.propertiesRaw$.value.flatMap(property => {
      const propertyMeta = this.dataSource.propertyMetaGet(
        property.type$.value
      );
      if (!propertyMeta) {
        return [];
      }
      return {
        id: property.id,
        name: property.name$.value,
        type: propertyMeta.config.jsonValue.type({
          data: property.data$.value,
          dataSource: this.dataSource,
        }),
        icon: property.icon,
        propertyType: property.type$.value,
      };
    });
  });

  protected get dataSource() {
    return this.manager.dataSource;
  }

  protected getBoundedPropertyIdsForView(): string[] {
    const limit = clampDataSourceWindowLimit(
      this.dataSource.columnCount$.value,
      DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT
    );
    const offset = clampDataSourceWindowOffset(
      this.dataSource.columnCount$.value,
      this.propertyWindowOffset$.value,
      limit
    );
    return this.dataSource.getPropertyIdsWindow({ offset, limit }).propertyIds;
  }

  get featureFlags$() {
    return this.dataSource.featureFlags$;
  }

  protected getProjectionFilter(): FilterGroup | undefined {
    return undefined;
  }

  protected getProjectionGroupBy(): GroupBy | readonly GroupBy[] | undefined {
    return undefined;
  }

  protected getProjectionSort(): Sort | undefined {
    return undefined;
  }

  protected getProjectionQuery(
    query: DatabaseProjectionQuery
  ): DatabaseProjectionQuery {
    const searchText = this.searchString.value.trim();
    return {
      ...query,
      filter: query.filter ?? this.getProjectionFilter(),
      groupBy: query.groupBy ?? this.getProjectionGroupBy(),
      searchText: query.searchText ?? (searchText || undefined),
      viewId: query.viewId ?? this.id,
      viewMode: query.viewMode ?? this.type,
      viewSort: query.viewSort ?? this.getProjectionSort(),
    };
  }

  get isLocked() {
    return this.lockRows$.value;
  }

  get meta() {
    return this.dataSource.viewMetaGet(this.type);
  }

  get propertyMetas$() {
    return this.dataSource.propertyMetas$;
  }

  abstract get type(): string;

  constructor(
    public manager: ViewManager,
    public id: string
  ) {}

  private searchRowsMapping(rows: Row[], searchString: string): Row[] {
    const normalizedSearch = searchString.trim().toLowerCase();
    const searchablePropertyIds = normalizedSearch
      ? this.dataSource.getAllPropertyIds()
      : [];
    return rows.filter(row => {
      if (normalizedSearch) {
        const containsSearchString = searchablePropertyIds.some(propertyId =>
          this.cellGetOrCreate(row.rowId, propertyId)
            .stringValue$.value?.toLowerCase()
            .includes(normalizedSearch)
        );
        if (!containsSearchString) {
          return false;
        }
      }
      return this.isShow(row.rowId);
    });
  }

  cellGetOrCreate(rowId: string, propertyId: string): Cell {
    return new CellBase(this, propertyId, rowId);
  }

  setRowWindowOffset(offset: number): void {
    const limit = this.rowWindowLimit$.value;
    const rowOffset = clampDataSourceWindowOffset(
      this.dataSource.rowCount$.value,
      Math.max(0, offset),
      limit
    );
    this.setLargeDataWindowPatch({ rowOffset });
  }

  setRowWindowLimit(limit: number): void {
    const rowLimit = clampConfiguredWindowLimit(
      limit,
      DATABASE_LARGE_DATA_POLICY.ui.maxPageSize
    );
    this.setLargeDataWindowPatch({
      rowLimit,
      rowOffset: clampDataSourceWindowOffset(
        this.dataSource.rowCount$.value,
        this.rowWindowOffset$.value,
        rowLimit
      ),
    });
  }

  setPropertyWindowOffset(offset: number): void {
    const limit = this.propertyWindowLimit$.value;
    const propertyOffset = clampDataSourceWindowOffset(
      this.dataSource.columnCount$.value,
      Math.max(0, offset),
      limit
    );
    this.setLargeDataWindowPatch({ propertyOffset });
  }

  setPropertyWindowLimit(limit: number): void {
    const propertyLimit = clampConfiguredWindowLimit(
      limit,
      DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT
    );
    this.setLargeDataWindowPatch({
      propertyLimit,
      propertyOffset: clampDataSourceWindowOffset(
        this.dataSource.columnCount$.value,
        this.propertyWindowOffset$.value,
        propertyLimit
      ),
    });
  }

  getRowsWindow(request: DataSourceWindowRequest): SingleViewRowsWindow {
    const query: DatabaseProjectionQuery = {
      scope: 'filtered-result',
      window: request,
    };
    if (!this.shouldProjectRowsWindow(query)) {
      const sourceWindow = this.dataSource.getRowsWindow(request);
      return {
        ...sourceWindow,
        rowIds: sourceWindow.rowIds.slice(),
        rows: sourceWindow.rowIds.map(rowId => this.rowGetOrCreate(rowId)),
      };
    }

    const projection = this.projectRows(query);
    return {
      offset: projection.window.offset,
      limit: projection.window.limit,
      totalCount: projection.totalCount,
      hasMore:
        projection.window.offset + projection.window.limit <
        projection.totalCount,
      rowIds: projection.rowIds.slice(),
      rows: projection.rows,
    };
  }

  private shouldProjectRowsWindow(query: DatabaseProjectionQuery): boolean {
    const projectionQuery = this.getProjectionQuery(query);
    return Boolean(
      projectionQuery.searchText?.trim() ||
      projectionQuery.filter?.conditions.length ||
      projectionQuery.predicates?.length ||
      projectionQuery.aggregates?.length ||
      projectionQuery.sort?.length ||
      projectionQuery.viewSort?.sortBy.length ||
      projectionQuery.viewSort?.manuallySort.length
    );
  }

  projectRows(query: DatabaseProjectionQuery): SingleViewProjectionResult {
    const projectionQuery = this.getProjectionQuery(query);
    const key = projectionCacheKey(this.id, projectionQuery);
    return getOrCreateProjectionCacheEntry(this, key, () =>
      this.executeProjection(projectionQuery)
    ).value;
  }

  private executeProjection(
    projectionQuery: DatabaseProjectionQuery
  ): SingleViewProjectionResult {
    const performanceScope = getDataSourcePerformanceScope(this.dataSource);
    let itemCount = 0;
    const execute = () => {
      const sourceProjection = this.dataSource.getProjectionSource({
        scope: projectionQuery.scope,
        viewId: projectionQuery.viewId,
        viewMode: projectionQuery.viewMode,
      });
      const propertyIds = getProjectionPropertyIds(
        projectionQuery,
        this.dataSource.getAllPropertyIds()
      );
      const candidateRowIds = projectionQuery.searchText?.trim()
        ? this.dataSource.getSearchCandidateRowIds(
            sourceProjection.rowIds,
            projectionQuery.searchText,
            propertyIds
          )
        : sourceProjection.rowIds;
      const result = projectDatabaseRows({
        complete: sourceProjection.complete,
        freshness: sourceProjection.freshness,
        propertyIds,
        query: projectionQuery,
        readCellValue: (rowId, propertyId) =>
          this.readProjectionCellValue(rowId, propertyId),
        rowIds: candidateRowIds,
        source: sourceProjection.source,
        syncState: sourceProjection.syncState,
      });
      const projection = {
        ...result,
        diagnostics: [...sourceProjection.diagnostics, ...result.diagnostics],
        rows: result.rowIds.map(rowId => this.rowGetOrCreate(rowId)),
      };
      itemCount = projection.rowIds.length;
      return projection;
    };
    return performanceScope
      ? measureGfxPerformance(
          performanceScope,
          'database-projection',
          execute,
          () => itemCount
        )
      : execute();
  }

  protected isRowVisibleByFilter(
    rowId: string,
    filter: FilterGroup | undefined
  ): boolean {
    if (!filter?.conditions.length) return true;
    const row = Object.fromEntries(
      this.dataSource
        .getAllPropertyIds()
        .map(propertyId => [
          propertyId,
          this.readProjectionCellValue(rowId, propertyId),
        ])
    );
    return evalFilter(filter, row);
  }

  private readProjectionCellValue(rowId: string, propertyId: string): unknown {
    return this.cellRawValueToJson(
      propertyId,
      this.dataSource.cellValueGet(rowId, propertyId)
    );
  }

  getBoundedRowsWindow(): SingleViewRowsWindow {
    const limit = this.rowWindowLimit$.value;
    const offset = clampDataSourceWindowOffset(
      this.dataSource.rowCount$.value,
      this.rowWindowOffset$.value,
      limit
    );
    return this.getRowsWindow({
      offset,
      limit,
    });
  }

  getRowIdsWindow(request: DataSourceWindowRequest): DataSourceRowsWindow {
    const { rows, ...window } = this.getRowsWindow(request);
    void rows;
    return window;
  }

  getBoundedRowIdsWindow(): DataSourceRowsWindow {
    const { rows, ...window } = this.getBoundedRowsWindow();
    void rows;
    return window;
  }

  getPropertiesWindow(
    request: DataSourceWindowRequest
  ): SingleViewPropertiesWindow {
    const sourceWindow = this.dataSource.getPropertyIdsWindow(request);
    const windowProperties = sourceWindow.propertyIds
      .map(id => this.propertyGetOrCreate(id))
      .filter(property => !property.hide$.value);
    return {
      offset: sourceWindow.offset,
      limit: sourceWindow.limit,
      totalCount: sourceWindow.totalCount,
      propertyIds: windowProperties.map(property => property.id),
      properties: windowProperties,
    };
  }

  getBoundedPropertiesWindow(): SingleViewPropertiesWindow {
    const limit = this.propertyWindowLimit$.value;
    const offset = clampDataSourceWindowOffset(
      this.columnCount$.value,
      this.propertyWindowOffset$.value,
      limit
    );
    return this.getPropertiesWindow({
      offset,
      limit,
    });
  }

  getRawPropertiesWindow(
    request: DataSourceWindowRequest
  ): SingleViewPropertiesWindow {
    const sourceWindow = this.dataSource.getPropertyIdsWindow(request);
    const windowProperties = sourceWindow.propertyIds.map(id =>
      this.propertyGetOrCreate(id)
    );
    return {
      offset: sourceWindow.offset,
      limit: sourceWindow.limit,
      totalCount: sourceWindow.totalCount,
      propertyIds: windowProperties.map(property => property.id),
      properties: windowProperties,
    };
  }

  getBoundedRawPropertiesWindow(): SingleViewPropertiesWindow {
    const limit = this.propertyWindowLimit$.value;
    const offset = clampDataSourceWindowOffset(
      this.columnCount$.value,
      this.propertyWindowOffset$.value,
      limit
    );
    return this.getRawPropertiesWindow({
      offset,
      limit,
    });
  }

  protected cellRawValueToJson(propertyId: string, value: unknown): unknown {
    const property = this.propertyGetOrCreate(propertyId);
    const toJson = property.meta$.value?.config.rawValue.toJson;
    if (!toJson) {
      return undefined;
    }
    return (
      toJson({
        value,
        data: property.data$.value,
        dataSource: this.dataSource,
      }) ?? undefined
    );
  }

  getCellsWindow(request: DataSourceCellsWindowRequest): DataSourceCellsWindow {
    const rowsWindow = this.getRowsWindow(request);
    const propertySource =
      request.propertyIds ?? this.getBoundedPropertiesWindow().propertyIds;
    const { propertyOffset, propertyLimit, propertyIds } =
      normalizeDataSourcePropertyWindow(request, propertySource);
    const cells: Record<string, Record<string, unknown>> = {};

    for (const rowId of rowsWindow.rowIds) {
      const rowCells: Record<string, unknown> = {};
      for (const propertyId of propertyIds) {
        rowCells[propertyId] = this.cellRawValueToJson(
          propertyId,
          this.dataSource.cellValueGet(rowId, propertyId)
        );
      }
      cells[rowId] = rowCells;
    }

    return {
      offset: rowsWindow.offset,
      limit: rowsWindow.limit,
      propertyOffset,
      propertyLimit,
      totalRowCount: rowsWindow.totalCount,
      totalColumnCount: this.columnCount$.value,
      rowIds: rowsWindow.rowIds,
      propertyIds,
      cells,
    };
  }

  serviceGet<T>(key: GeneralServiceIdentifier<T>): T | null {
    return this.dataSource.serviceGet(key);
  }

  serviceGetOrCreate<T>(key: GeneralServiceIdentifier<T>, create: () => T): T {
    return this.dataSource.serviceGetOrCreate(key, create);
  }

  dataUpdate(updater: (viewData: ViewData) => Partial<ViewData>): void {
    this.dataSource.viewDataUpdate(this.id, updater);
  }

  delete(): void {
    this.manager.viewDelete(this.id);
  }

  duplicate(): void {
    this.manager.viewDuplicate(this.id);
  }

  abstract isShow(rowId: string): boolean;

  lockRows(lock: boolean) {
    this.lockRows$.value = lock;
  }

  nameSet(name: string): void {
    this.dataUpdate(() => {
      return {
        name,
      } as ViewData;
    });
  }

  propertyAdd(
    position: InsertToPosition,
    ops?: {
      type?: string;
      name?: string;
    }
  ): string | undefined {
    const id = this.dataSource.propertyAdd(position, ops);
    if (!id) {
      return;
    }
    const property = this.propertyGetOrCreate(id);
    property.move(position);
    return id;
  }

  abstract propertyGetOrCreate(propertyId: string): Property;

  rowAdd(insertPosition: InsertToPosition | number): string {
    this.lockRows(false);
    return this.dataSource.rowAdd(insertPosition);
  }

  rowGetOrCreate(rowId: string): Row {
    return new RowBase(this, rowId);
  }

  protected rowsMapping(rows: Row[]): Row[] {
    return this.searchRowsMapping(rows, this.searchString.value);
  }

  setSearch(str: string): void {
    const previousSearch = this.searchString.value;
    if (previousSearch === str) {
      return;
    }
    this.searchString.value = str;
    // Search changes the result window, so a previously valid later page can
    // become empty even though matching rows exist. Always begin a new search
    // at its first page; the configured page size remains unchanged.
    this.setRowWindowOffset(
      resolveSearchRowWindowOffset(
        previousSearch,
        str,
        this.rowWindowOffset$.value
      )
    );
  }

  traitGet<T>(key: TraitKey<T>): T | undefined {
    return this.traitMap.get(key.key) as T | undefined;
  }

  protected traitSet<T>(key: TraitKey<T>, value: T): T {
    this.traitMap.set(key.key, value);
    return value;
  }
}
